import React from 'react';
import { Share2, Download, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUnits } from '../../context/UnitContext';

/**
 * ShareCardGenerator
 * A utility to generate high-contrast, branded screenshot-friendly cards
 * for social sharing (Instagram Stories, Twitter, etc.)
 */
const ShareCard = ({ type, data, onClose }) => {
    const { t } = useTranslation();
    const { displayWeight, unitLabel } = useUnits();
    const [copied, setCopied] = React.useState(false);

    const handleCopyText = () => {
        let text = '';
        if (type === 'achievement') {
            text = `🏆 ${t('achievements.unlocked')}: ${data.title}\n\n${data.description}\n\n#GymTrackerPro #Fitness #Achievements`;
        } else if (type === 'pr') {
            text = `💪 ${t('records.new_pr')}: ${data.lift} - ${displayWeight(data.value)}\n\n#GymTrackerPro #PR #Progress #Powerlifting`;
        }

        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-6 animate-in fade-in duration-300">
            {/* The Actual Card to Screenshot */}
            <div id="share-card-container" className="w-full max-w-sm aspect-[9/16] bg-black border-4 border-red-600 rounded-[2rem] overflow-hidden relative shadow-2xl shadow-red-600/20">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-red-600/20 via-transparent to-transparent" />
                </div>

                {/* Content */}
                <div className="h-full flex flex-col p-8 justify-between relative z-10">
                    {/* Header */}
                    <div className="flex justify-between items-start">
                        <div className="bg-red-600 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">
                            Gym Tracker Pro
                        </div>
                        <div className="text-neutral-500 font-mono text-[10px]">
                            {new Date().toLocaleDateString()}
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 flex flex-col items-center justify-center text-center space-y-6">
                        <div className="w-24 h-24 bg-red-600/20 rounded-3xl flex items-center justify-center border-2 border-red-600 animate-pulse">
                            {type === 'achievement' ? (
                                <span className="text-5xl">{data.icon || '🏆'}</span>
                            ) : (
                                <Trophy className="text-red-500" size={48} />
                            )}
                        </div>

                        <div>
                            <h2 className="text-xs font-black text-red-500 uppercase tracking-[0.2em] mb-2">
                                {type === 'achievement' ? t('achievements.unlocked') : t('records.new_pr')}
                            </h2>
                            <h1 className="text-3xl font-black text-white italic uppercase tracking-tighter leading-none mb-4">
                                {type === 'achievement' ? data.title : data.lift}
                            </h1>
                            {type === 'pr' && (
                                <div className="flex items-baseline justify-center gap-2">
                                    <span className="text-6xl font-black text-white">{displayWeight(data.value, false)}</span>
                                    <span className="text-neutral-500 text-xl font-bold">{unitLabel}</span>
                                </div>
                            )}
                            {type === 'achievement' && (
                                <p className="text-neutral-400 font-medium text-sm px-4">
                                    {data.description}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Footer / QR Placeholder */}
                    <div className="border-t border-neutral-800 pt-6 flex items-center justify-between">
                        <div className="text-left">
                            <p className="text-[10px] text-neutral-500 font-bold uppercase tracking-wider">Track your progress at</p>
                            <p className="text-red-500 font-black text-sm">gymtrackerpro.app</p>
                        </div>
                        <div className="w-12 h-12 bg-white rounded-lg p-1 opacity-80">
                            {/* In a real app, this would be a QR code to the user's profile or the app store */}
                            <div className="w-full h-full bg-black rounded-[2px]" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex gap-4 w-full max-w-sm">
                <button
                    onClick={handleCopyText}
                    className="flex-1 bg-neutral-900 border border-neutral-800 p-4 rounded-2xl flex items-center justify-center gap-2 text-white font-bold hover:bg-neutral-800 transition-all"
                >
                    {copied ? <Check size={20} className="text-emerald-500" /> : <Copy size={20} />}
                    {copied ? t('common.copied') : t('common.copy_text')}
                </button>
                <button
                    onClick={onClose}
                    className="bg-red-600 p-4 rounded-2xl text-white font-bold hover:bg-red-500 transition-all px-8"
                >
                    {t('common.close')}
                </button>
            </div>

            <p className="mt-4 text-neutral-500 text-xs text-center px-8">
                {t('share.screenshot_tip')}
            </p>
        </div>
    );
};

const Trophy = ({ className, size }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
);

export default ShareCard;
